/**
 * Trips Service
 * Frontend service för att hämta trippar från API
 */

import api from '../api/client';

export interface Trip {
  tripId: string;
  vehicleId: string;
  driverId?: string;
  organizationId?: string;
  startTime: string;
  endTime?: string;
  startLocation: {
    type: 'Point';
    coordinates: [number, number]; // [lng, lat]
  };
  endLocation?: {
    type: 'Point';
    coordinates: [number, number]; // [lng, lat]
  };
  // Adresser
  startAddress?: string;
  endAddress?: string;
  // Avstånd och tid
  distance: number; // meter
  duration?: number; // sekunder
  status: 'active' | 'completed' | 'cancelled';
  routePointCount: number;
  averageSpeed?: number;
  maxSpeed?: number;
  // Bränsle (AVL 12 - Fuel Used GPS)
  fuelConsumption?: number; // Beräknad förbrukning i liter
  startFuelUsed?: number; // liter (kumulativt vid start)
  endFuelUsed?: number; // liter (kumulativt vid slut)
  /** PHEV/EV: GPS-sträcka i eldrift (km) */
  electricDistanceKm?: number;
  /** PHEV/EV: andel av trippsträcka i eldrift (%) */
  electricSharePct?: number;
  driveModeSource?: 'hv_current' | 'rpm' | 'mixed' | 'bev';
  /** Ren BEV — API döljer fossilbränsle och sätter 100 % el */
  isPureElectric?: boolean;
  // Odometer (AVL 16 - Total Odometer)
  startOdometer?: number; // meter
  endOdometer?: number; // meter
  // OEM värden (AVL 389)
  oemTotalOdometer?: number; // meter
  // VIN
  vin?: string;
  // Privacy
  purpose?: 'business' | 'private';
  isPrivate?: boolean;
  // Rekonstruerad (GPS-lös) tripp: härledd från tändning + positionshopp
  reconstructed?: boolean;
  distanceSource?: 'gps' | 'oem' | 'osrm' | 'straight';
  /** Antal körbeteendelarm (harsh/overspeed/idling) från eco_driving_events */
  alarmCount?: number;
  alarmBreakdown?: {
    harsh_acceleration: number;
    harsh_braking: number;
    harsh_cornering: number;
    overspeed: number;
    excessive_idling: number;
  };
}

export interface TripEcoEvent {
  eventId?: string;
  tripId?: string;
  timestamp: string;
  eventType: string;
  severity?: number;
  location?: {
    type: string;
    coordinates: [number, number];
  };
}

export interface TripEcoEventsResponse {
  success: boolean;
  data: TripEcoEvent[];
  alarmCount: number;
  breakdown?: Trip['alarmBreakdown'];
  count?: number;
  error?: string;
}

export interface TripRoutePoint {
  timestamp: string;
  coordinates: [number, number]; // [lng, lat] GeoJSON format
  speed?: number;
  /** AVL 199 – tripp-odometer i meter (från enheten) */
  tripOdometer?: number | null;
  heading?: number;
  altitude?: number;
  satellites?: number;
  canData?: any;
  ioStatus?: any;
  speedLimit?: number | null; // Hastighetsgräns från geo-services (km/h)
  speedExcess?: number | null; // km/h över hastighetsgränsen (null om inte för fort)
  roadName?: string | null; // Vägnamn från geo-services
  roadType?: string | null; // Vägtyp (motorway, residential, etc.)
  speedLimitSource?: string | null; // Källa (sign/default)
  /** AVL 249 – GSM jamming aktiv på denna GPS-punkt */
  jamming?: boolean;
}

export interface TripRoute {
  tripId: string;
  pointCount: number;
  route: TripRoutePoint[];
  /** Flera segment när GPS-punkter saknas (undvik rakt streck över luckor). */
  routeSegments?: TripRoutePoint[][];
  firstTimestamp: string;
  lastTimestamp: string;
  /** gps_chunks = full spår; trip_route = inbäddad subsample; trip_endpoints = bara start→slut */
  source?: 'gps_chunks' | 'trip_route' | 'trip_endpoints';
}

export interface TripsResponse {
  success: boolean;
  data: Trip[];
  pagination?: {
    total: number;
    limit: number;
    offset: number;
  };
  error?: string;
}

export interface TripResponse {
  success: boolean;
  data: Trip;
  error?: string;
}

export interface TripRouteResponse {
  success: boolean;
  data: TripRoute;
  error?: string;
}

/** En rad från trip-bin-data (AVL-parametrar med beskrivning och värde) */
export interface TripBinDataRow {
  timestamp: string;
  avlId: number;
  parameterName: string;
  description: string;
  value: string | number;
}

export interface TripBinDataResponse {
  success: boolean;
  data?: {
    tripId: string;
    imei: string;
    startTime: string;
    endTime: string;
    rowCount: number;
    rows: TripBinDataRow[];
  };
  error?: string;
}

class TripsService {
  /**
   * Hämta trippar med filter
   */
  async getTrips(params: {
    vehicleId?: string;
    driverId?: string;
    organizationId?: string;
    from?: string;
    to?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ trips: Trip[]; pagination?: TripsResponse['pagination'] }> {
    try {
      const queryParams = new URLSearchParams();
      if (params.vehicleId) queryParams.append('vehicleId', params.vehicleId);
      if (params.driverId) queryParams.append('driverId', params.driverId);
      if (params.organizationId) queryParams.append('organizationId', params.organizationId);
      if (params.from) queryParams.append('from', params.from);
      if (params.to) queryParams.append('to', params.to);
      if (params.status) queryParams.append('status', params.status);
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.offset) queryParams.append('offset', params.offset.toString());

      const response = await api.get<TripsResponse>(`/api/trips?${queryParams.toString()}`);
      if (response.data.success) {
        return {
          trips: response.data.data,
          pagination: response.data.pagination,
        };
      }
      throw new Error(response.data.error || 'Failed to fetch trips');
    } catch (error: any) {
      console.error('Error fetching trips:', error);
      throw error;
    }
  }

  /**
   * Hämta en specifik tripp
   */
  async getTrip(tripId: string): Promise<Trip> {
    try {
      const response = await api.get<TripResponse>(`/api/trips/${tripId}`);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to fetch trip');
    } catch (error: any) {
      console.error('Error fetching trip:', error);
      throw error;
    }
  }

  /**
   * Hämta GPS-punkter för en tripp (route)
   */
  async getTripRoute(tripId: string, options?: { signal?: AbortSignal }): Promise<TripRoute> {
    try {
      const response = await api.get<TripRouteResponse>(`/api/trips/${tripId}/route`, {
        signal: options?.signal,
      });
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to fetch trip route');
    } catch (error: any) {
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || options?.signal?.aborted) {
        throw error;
      }
      console.error('Error fetching trip route:', error);
      throw error;
    }
  }

  /**
   * Hämta bin-data för en tripp (AVL-parametrar med beskrivning) – endast global admin.
   */
  async getTripBinData(tripId: string): Promise<TripBinDataResponse['data']> {
    const response = await api.get<TripBinDataResponse>('/api/trips/bin-data', {
      params: { tripId },
    });
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Kunde inte hämta bin-data');
  }

  /**
   * Hämta körbeteendelarm för en tripp (harsh/overspeed/idling).
   */
  async getTripEcoEvents(tripId: string): Promise<{
    events: TripEcoEvent[];
    alarmCount: number;
    breakdown?: Trip['alarmBreakdown'];
  }> {
    const response = await api.get<TripEcoEventsResponse>(
      `/api/trips/${encodeURIComponent(tripId)}/eco-events`
    );
    if (response.data.success) {
      return {
        events: response.data.data || [],
        alarmCount: response.data.alarmCount ?? response.data.count ?? 0,
        breakdown: response.data.breakdown,
      };
    }
    throw new Error(response.data.error || 'Failed to fetch trip eco events');
  }
}

export const tripsService = new TripsService();
export default tripsService;

